import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

import SEO from '@/components/SEO';
import { indianArtStars } from '@/lib/indianArtStars';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const featuredTraditions = [
  'Terracotta Pottery',
  'Ikat Print and Weave',
  'Pattachitra',
  'Lacquered Wooden Toys',
  'Ornate Tables and Woodcraft',
  'Blackstone Pottery',
  'Warli Painting',
  'Madhubani Painting',
  'Gond Art',
  'Kalamkari',
  'Thanjavur Painting',
  'Phad Painting',
];

const whyItMatters = [
  'Many Indian art forms stay rooted in family workshops, regional lineages, and local teaching circles, but still do not get enough digital visibility.',
  'Collectors, institutions, cultural curators, festival organizers, and younger audiences often discover only a narrow slice of Indian art online.',
  'KalaManch can help artists preserve their story, build a portfolio, and become discoverable beyond geography.',
];

export default function IndianArtPage() {
  const [stars, setStars] = useState(indianArtStars);

  useEffect(() => {
    let mounted = true;
    const loadStars = async () => {
      try {
        const response = await axios.get(`${API}/v1/indian-art-stars`);
        const nextStars = response.data?.stars || [];
        if (mounted && nextStars.length > 0) {
          setStars(nextStars);
        }
      } catch (error) {
        if (mounted) {
          setStars(indianArtStars);
        }
      }
    };
    loadStars();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-28">
      <SEO
        title="Stars of Indian Art | KalaManch.art"
        description="Discover curated Indian artists, rare visual traditions, and star artist stories on KalaManch.art. Explore detailed artist pages, artworks, and heritage-driven creative profiles."
        keywords="Stars of Indian Art, Indian art traditions, curated Indian artists, KalaManch Indian art, rare Indian art forms, heritage artists India"
      />

      <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-orange-100 bg-white shadow-xl">
          <div className="grid gap-0 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="bg-gradient-to-br from-[#fff7ed] via-white to-[#e8fff8] px-6 py-10 sm:px-10 lg:px-12">
              <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#FF6F00]">Managed by KalaManch Admin</p>
              <h1 className="mt-4 max-w-4xl text-4xl font-extrabold leading-tight text-gray-950 sm:text-5xl lg:text-6xl">
                Stars of Indian Art on KalaManch
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-gray-700">
                A dedicated KalaManch space for Indian artists whose work carries heritage, place, culture, spirituality, craft, and visual depth that deserve stronger digital discovery.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <span className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-bold text-[#C45A00]">
                  Curated artist pages
                </span>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700">
                  Virtual gallery storytelling
                </span>
                <span className="rounded-full border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-bold text-purple-700">
                  Indian art beyond the obvious
                </span>
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/artists" className="btn-primary">
                  Explore Artists
                </Link>
                <Link to="/indian-art#featured-stars" className="btn-outline">
                  View curated stories
                </Link>
              </div>
            </div>

            <div className="bg-[#0F172A] px-6 py-10 text-white sm:px-10 lg:px-10">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#FDBA74]">Why This Matters</p>
              <div className="mt-5 grid gap-4">
                {whyItMatters.map((item) => (
                  <div key={item} className="rounded-[1.4rem] border border-white/10 bg-white/5 p-4 text-sm font-semibold leading-7 text-slate-100">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="featured-stars" className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#00A68C]">Featured now</p>
            <h2 className="mt-2 text-3xl font-extrabold text-gray-950">Curated artist stories</h2>
          </div>
        </div>
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {stars.map((star) => (
            <article key={star.slug} className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="grid gap-0 md:grid-cols-[0.42fr_0.58fr]">
                <div className="bg-[#f8fafc]">
                  <img
                    src={star.coverArtwork || star.portrait}
                    alt={star.name}
                    className="h-full min-h-[18rem] w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#FF6F00]">{star.heading}</p>
                  <h3 className="mt-3 text-3xl font-extrabold text-gray-950">{star.name}</h3>
                  <p className="mt-2 text-base font-bold text-[#4A148C]">{star.specialty}</p>
                  <p className="mt-4 text-base leading-7 text-gray-700">{star.shortIntro}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {star.heroBadges.map((badge) => (
                      <span key={badge} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                        {badge}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                    <Link to={`/indian-art/stars/${star.slug}`} className="btn-primary">
                      View artist story
                    </Link>
                    <Link to={star.profilePath} className="btn-outline">
                      Open KalaManch profile
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#4A148C]">Indian art forms to spotlight</p>
          <h2 className="mt-3 text-3xl font-extrabold text-gray-950">A growing discovery layer for overlooked traditions</h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {featuredTraditions.map((form) => (
              <span
                key={form}
                className="rounded-full border border-slate-200 bg-[#f8fafc] px-4 py-2 text-sm font-bold text-slate-800 shadow-sm"
              >
                {form}
              </span>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
